/* ----------------------------------------------------------------------------
 * voiceLeaseApi — typed client for the single-active-voice-session lease
 * (GET/POST /v1/ai/aura/voice-lease). SELF-CONTAINED for the same reason as
 * voiceApi.ts: the voice net tests run under Node's native --test (strip-only
 * TS) where `@/lib/api` cannot load, so this module faithfully REPLICATES
 * api.ts's base-URL + credentials + CSRF double-submit behaviour with a
 * Node-safe base-URL accessor. The ONLY credential sent is the session cookie
 * (+ the bc_csrf double-submit token on the POST). No provider key, ever.
 *
 * FAIL-OPEN contract: callers TREAT ANY throw from here as "arm as today"
 * (gate 'open'). The lease is a UX nicety, never tenant data — a broken arbiter
 * must degrade to current single-device behaviour, not kill voice.
 * -------------------------------------------------------------------------- */
import type { LeasePostResponse, LeaseHolder } from '../wake/leaseMachine.ts';

function resolveApiBase(): string {
  try {
    const meta = import.meta as unknown as { env?: Record<string, string | undefined> };
    const fromEnv = meta.env ? meta.env.VITE_API_BASE : undefined;
    if (fromEnv) return fromEnv;
  } catch {
    /* Node --test: import.meta.env is absent — use the production default. */
  }
  return 'https://api.bookedcentral.com/v1';
}

const API_BASE = resolveApiBase();
const LEASE_PATH = '/ai/aura/voice-lease';

export interface LeaseStatusResponse {
  ok?: boolean;
  held?: boolean;
  holder?: LeaseHolder | null;
}

/** A lease transport failure — callers map this to fail-open (gate 'open'). */
export class VoiceLeaseError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'VoiceLeaseError';
    this.status = status;
    this.code = code;
  }
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function buildUrl(path: string): string {
  const base = API_BASE.replace(/\/$/, '');
  const rel = path.startsWith('/') ? path : `/${path}`;
  return `${base}${rel}`;
}

function mutationHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  const csrf = readCookie('bc_csrf');
  if (csrf) headers['X-CSRF-Token'] = csrf;
  return headers;
}

function safeJson(text: string): Record<string, unknown> | undefined {
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

async function post<T>(payload: Record<string, unknown>, signal?: AbortSignal): Promise<T> {
  let res: Response;
  try {
    res = await fetch(buildUrl(LEASE_PATH), {
      method: 'POST',
      headers: mutationHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload),
      signal,
    });
  } catch (networkErr) {
    if ((networkErr as Error)?.name === 'AbortError') throw networkErr;
    throw new VoiceLeaseError(0, 'network_error', (networkErr as Error).message || 'Network request failed');
  }
  const text = await res.text();
  const body = text ? safeJson(text) : undefined;
  if (!res.ok) {
    const nested = body && typeof body.error === 'object' && body.error !== null ? (body.error as Record<string, unknown>) : undefined;
    const code = (nested?.code as string) ?? (body?.code as string) ?? 'error';
    const message = (nested?.message as string) ?? res.statusText;
    throw new VoiceLeaseError(res.status, code, message);
  }
  return body as T;
}

/** POST acquire — claim (or, with takeover, seize) the lease for this client. */
export function acquireLease(clientId: string, clientLabel: string, takeover = false, signal?: AbortSignal): Promise<LeasePostResponse> {
  return post<LeasePostResponse>({ op: 'acquire', client_id: clientId, client_label: clientLabel, takeover }, signal);
}

/** POST renew — heartbeat: extend THIS client's own live lease. */
export function renewLease(clientId: string, signal?: AbortSignal): Promise<LeasePostResponse> {
  return post<LeasePostResponse>({ op: 'renew', client_id: clientId }, signal);
}

/** POST release — free the lease if we hold it (awaited, normal teardown). */
export function releaseLease(clientId: string, signal?: AbortSignal): Promise<LeasePostResponse> {
  return post<LeasePostResponse>({ op: 'release', client_id: clientId }, signal);
}

/** GET the current holder (null = free) — for a non-holder's pill refresh. */
export async function getLeaseStatus(signal?: AbortSignal): Promise<LeaseStatusResponse> {
  let res: Response;
  try {
    res = await fetch(buildUrl(LEASE_PATH), { method: 'GET', headers: { Accept: 'application/json' }, credentials: 'include', signal });
  } catch (networkErr) {
    if ((networkErr as Error)?.name === 'AbortError') throw networkErr;
    throw new VoiceLeaseError(0, 'network_error', (networkErr as Error).message || 'Network request failed');
  }
  const text = await res.text();
  const body = text ? safeJson(text) : undefined;
  if (!res.ok) throw new VoiceLeaseError(res.status, 'error', res.statusText);
  return (body ?? {}) as LeaseStatusResponse;
}

/**
 * Best-effort release on pagehide/unmount using a KEEPALIVE fetch (survives the
 * page teardown that would cancel a normal fetch). NOTE: navigator.sendBeacon
 * CANNOT carry the bc_csrf header the write requires, so a keepalive fetch (which
 * CAN) is the correct primitive; the 45s server TTL is the backstop if even this
 * is dropped. Fire-and-forget: never awaited, never throws.
 */
export function releaseLeaseKeepalive(clientId: string): void {
  try {
    if (typeof fetch !== 'function') return;
    void fetch(buildUrl(LEASE_PATH), {
      method: 'POST',
      headers: mutationHeaders(),
      credentials: 'include',
      body: JSON.stringify({ op: 'release', client_id: clientId }),
      keepalive: true,
    }).catch(() => { /* best-effort — TTL is the backstop */ });
  } catch {
    /* best-effort — TTL is the backstop */
  }
}
